import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { FilterFormLoading, PanelLoading } from "~/components/loading";
import type { ChangeRequestListFilters, ChangeRequestSortOrder, SortDirection } from "~/features/changeRequests/types";
import { ChangeRequestPeek } from "./ChangeRequestPeek";
import { ChangeRequestsSearchForm } from "./ChangeRequestsSearchForm";
import { ChangeRequestsTable } from "./ChangeRequestsTable";

interface ChangeRequestsPresenterProps {
  filters: ChangeRequestListFilters;
  onApplyFilters: (next: ChangeRequestListFilters) => void;
  sortOrder: ChangeRequestSortOrder;
  direction: SortDirection;
  onSortingChange: (order: ChangeRequestSortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  selectedChangeRequest: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

export function ChangeRequestsPresenter({
  filters,
  onApplyFilters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  selectedChangeRequest,
  onSelect,
  onCloseSelected,
}: ChangeRequestsPresenterProps) {
  const { t } = useTranslation();

  // Filter/search changes navigate (RR wraps it in a transition); keying the table's Suspense
  // boundary by the filter signature remounts it and shows the shimmer, while sort/paging keep
  // the key stable and fall through to the dimmed-rows pending state.
  const filterKey = JSON.stringify([
    filters.search,
    filters.statuses,
    filters.modelTypes,
    filters.hasReviewComments,
    filters.categoryIds,
    filters.dataImportProjectIds,
    filters.recordComposerDefinitionId,
    filters.batchChangeRequestsJobId,
    filters.batchChangeRequestsJobRunId,
  ]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <h1 className="text-dewey-title capitalize text-fg-1">{t("model.changeRequest.modelNames")}</h1>

      <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0 overflow-hidden border-r border-border-default pr-4">
          <Suspense fallback={<FilterFormLoading />}>
            <ChangeRequestsSearchForm filters={filters} onApply={onApplyFilters} />
          </Suspense>
        </aside>

        <main className="min-w-0">
          <Suspense key={filterKey} fallback={<PanelLoading />}>
            <ChangeRequestsTable
              filters={filters}
              sortOrder={sortOrder}
              direction={direction}
              onSortingChange={onSortingChange}
              pageIndex={pageIndex}
              onPageIndexChange={onPageIndexChange}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              isPending={isPending}
              onSelect={onSelect}
            />
          </Suspense>
        </main>
      </div>

      <ChangeRequestPeek id={selectedChangeRequest} onClose={onCloseSelected} />
    </div>
  );
}
